
import React, { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from '@/components/ui/enhanced/collapsible';

interface ValidationError {
  field: string;
  message: string;
}

interface ServerError {
  status: number;
  message: string;
  details?: string;
}

interface ErrorDisplayProps {
  validationErrors?: ValidationError[];
  serverError?: ServerError | null;
  error?: string; // Added simple string error option
  onRetry?: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  validationErrors = [],
  serverError = null,
  error = null,
  onRetry
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasErrors = validationErrors.length > 0 || serverError !== null || !!error;

  if (!hasErrors) {
    return null;
  }

  return (
    <div className="space-y-4 mb-6">
      {/* Display simple string error if provided */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="flex flex-col space-y-2">
              <span>{error}</span>
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  className="mt-2 w-fit"
                >
                  Prøv igjen
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="flex flex-col space-y-2">
              <span className="font-medium">Vennligst rett følgende feil:</span>
              <ul className="list-disc pl-5 space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>
                    <span className="font-medium">{error.field}:</span> {error.message}
                  </li>
                ))}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {serverError && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="flex flex-col space-y-2">
              <span className="font-medium">
                {serverError.status === 401
                  ? "Du er ikke autorisert til å utføre denne handlingen."
                  : serverError.status === 403
                  ? "Du har ikke tilgang til denne ressursen."
                  : serverError.status === 404
                  ? "Ressursen ble ikke funnet."
                  : serverError.status === 429
                  ? "For mange forespørsler. Vennligst prøv igjen senere."
                  : serverError.status >= 500
                  ? "En serverfeil oppstod. Vennligst prøv igjen senere."
                  : "Det oppstod en feil under behandling av forespørselen."}
              </span>
              <span>{serverError.message}</span>
              
              {serverError.details && (
                <Collapsible 
                  open={detailsOpen}
                  onOpenChange={setDetailsOpen}
                  className="w-full"
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-0 h-auto flex items-center text-sm font-normal underline hover:no-underline"
                    >
                      <ChevronDown
                        className={`h-4 w-4 mr-1 transition-transform ${
                          detailsOpen ? "rotate-180" : ""
                        }`}
                      />
                      {detailsOpen ? "Skjul detaljer" : "Vis detaljer"}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-2">
                    <div className="bg-destructive/10 p-2 rounded text-sm font-mono whitespace-pre-wrap">
                      {serverError.details}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  className="mt-2 w-fit"
                >
                  Prøv igjen
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ErrorDisplay;
